import React, { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { ProfileForm } from '../ProfileForm'
import { loadProfile, saveProfile } from '../../utils/profileStorage'

// ── Welcome (no selection) ──────────────────────────────────────────────────

function WelcomePane({ fileCount, onNewEval }) {
  return (
    <div className="vault-welcome">
      <div className="vault-welcome-logo">ark</div>
      <p className="vault-welcome-sub">your job evaluation vault</p>
      <div className="vault-welcome-stats">
        {fileCount} item{fileCount !== 1 ? 's' : ''} in pipeline
      </div>
      <button className="vault-welcome-btn" onClick={onNewEval}>
        + New Evaluation
      </button>
      <div className="vault-welcome-hint">
        Select a job from the sidebar, or browse Open Roles to send jobs here.
      </div>
    </div>
  )
}

// ── Profile ─────────────────────────────────────────────────────────────────

function ProfilePane() {
  const [profile, setProfile] = useState(() => loadProfile())
  return (
    <div className="vault-profile-pane">
      <ProfileForm
        profile={profile}
        onSave={(p) => { saveProfile(p); setProfile(p) }}
        onAutoSave={(p) => setProfile(p)}
        hideSaveRow={false}
      />
    </div>
  )
}

// ── Job detail (pipeline items) ─────────────────────────────────────────────

function JobDetailPane({ file, companyIntel, jobDescription, loadingDetail, onEvaluate }) {
  const job = file._data || {}
  const fm = file.frontmatter || {}
  const isEvaluated = fm.status === 'evaluated' || fm.score_letter
  const evalResult = job.evaluation_json

  return (
    <div className="vault-job-pane">
      <div className="vault-job-header">
        <h2 className="vault-job-title">{job.job_title || fm.title}</h2>
        <div className="vault-job-meta">
          <span className="vault-job-company">{job.company_name || fm.company}</span>
          {(fm.work_mode && fm.work_mode !== 'unspecified') && <span className="vault-job-badge">{fm.work_mode}</span>}
          {fm.industry && <span className="vault-job-badge">{fm.industry}</span>}
          {job.location && <span className="vault-job-location">{job.location}</span>}
        </div>
        {job.job_url && (
          <a href={job.job_url} className="vault-job-link" target="_blank" rel="noopener noreferrer">
            View original posting {'\u2197'}
          </a>
        )}
      </div>

      {/* Score badge if evaluated */}
      {isEvaluated && fm.score_letter && (
        <div className="vault-job-score-row">
          <span className="vault-eval-score" style={{ color: { A: '#7fc97f', B: '#58a6ff', C: '#e8c47f', D: '#e8a07f', F: '#ff8888' }[fm.score_letter] }}>
            {fm.score_letter}
          </span>
          <span className="vault-eval-score-label">
            {{ A: 'Strong Apply', B: 'Apply', C: 'Consider', D: 'Stretch', F: 'Skip' }[fm.score_letter]}
          </span>
        </div>
      )}

      {/* Company Intelligence */}
      {companyIntel && (
        <div className="vault-company-intel">
          <h3>Company Intelligence</h3>
          <div className="vault-intel-grid">
            <div className="vault-intel-stat">
              <span className="vault-intel-num">{companyIntel.total_jobs}</span>
              <span className="vault-intel-label">total jobs</span>
            </div>
            <div className="vault-intel-stat">
              <span className="vault-intel-num">{companyIntel.jobs_last_7d}</span>
              <span className="vault-intel-label">this week</span>
            </div>
            <div className="vault-intel-stat">
              <span className="vault-intel-num">{companyIntel.jobs_last_30d}</span>
              <span className="vault-intel-label">this month</span>
            </div>
            <div className="vault-intel-stat">
              <span className="vault-intel-num">{companyIntel.industries}</span>
              <span className="vault-intel-label">industries</span>
            </div>
          </div>
          {companyIntel.industry_breakdown?.length > 0 && (
            <div className="vault-intel-industries">
              {companyIntel.industry_breakdown.map(ib => (
                <span key={ib.industry} className="vault-intel-tag">{ib.industry} ({ib.count})</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Job Description */}
      {loadingDetail && <div className="vault-job-loading">Loading job details...</div>}
      {!loadingDetail && jobDescription && (
        <div className="vault-job-description">
          <h3>Job Description</h3>
          <div className="vault-md-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {jobDescription}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {/* Evaluate button */}
      {!isEvaluated && !loadingDetail && (
        <button className="vault-eval-run-btn" onClick={onEvaluate} style={{ marginTop: '1.5rem' }}>
          Evaluate Fit {'\u2192'}
        </button>
      )}

      {/* Inline eval result if already evaluated */}
      {evalResult && (
        <div className="vault-job-eval-result">
          <h3>Evaluation</h3>
          <div className="vault-eval-block"><p>{evalResult.role_summary}</p></div>
          {evalResult.cv_match && (
            <div className="vault-eval-block">
              <h4>CV Match — {evalResult.cv_match.match_percent}%</h4>
              <div className="vault-eval-match">
                <div>
                  <div className="vault-eval-sublabel">Strengths</div>
                  <ul>{(evalResult.cv_match.strengths || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
                </div>
                <div>
                  <div className="vault-eval-sublabel">Gaps</div>
                  <ul className="vault-eval-gaps">{(evalResult.cv_match.gaps || []).map((g, i) => <li key={i}>{g}</li>)}</ul>
                </div>
              </div>
            </div>
          )}
          {evalResult.recommendation && (
            <div className="vault-eval-block">
              <h4>Recommendation</h4>
              <p style={{ fontWeight: 600 }}>{evalResult.recommendation}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Story detail ────────────────────────────────────────────────────────────

function StoryPane({ file }) {
  const s = file._data || {}
  return (
    <div className="vault-story-pane">
      <h2 className="vault-story-title">{s.title}</h2>
      {s.competency_tags?.length > 0 && (
        <div className="vault-story-tags">
          {s.competency_tags.map(t => <span key={t} className="vault-eval-tag">{t}</span>)}
        </div>
      )}
      <div className="vault-story-sections">
        {s.situation && <div className="vault-story-section"><h3>Situation</h3><p>{s.situation}</p></div>}
        {s.task && <div className="vault-story-section"><h3>Task</h3><p>{s.task}</p></div>}
        {s.action && <div className="vault-story-section"><h3>Action</h3><p>{s.action}</p></div>}
        {s.result && <div className="vault-story-section"><h3>Result</h3><p>{s.result}</p></div>}
        {s.reflection && <div className="vault-story-section"><h3>Reflection</h3><p>{s.reflection}</p></div>}
      </div>
    </div>
  )
}

// ── Main export ─────────────────────────────────────────────────────────────

export function ContentPane({ file, fileCount, companyIntel, jobDescription, loadingDetail, onNewEval, onEvaluate }) {
  if (!file) {
    return <WelcomePane fileCount={fileCount} onNewEval={onNewEval} />
  }

  if (file._source === 'profile' || file.frontmatter?.type === 'profile') {
    return <ProfilePane />
  }

  if (file._source === 'queue' || file._source === 'pipeline') {
    return (
      <JobDetailPane
        file={file}
        companyIntel={companyIntel}
        jobDescription={jobDescription}
        loadingDetail={loadingDetail}
        onEvaluate={onEvaluate}
      />
    )
  }

  if (file._source === 'story') {
    return <StoryPane file={file} />
  }

  return <WelcomePane fileCount={fileCount} onNewEval={onNewEval} />
}
